import React, { useState } from 'react';
import { Award, ShieldCheck, Settings, ArrowRight, Eye, Flame, Compass, Users, Sun } from 'lucide-react';

import about1Img from '../assets/about1.png'; // Adjust path if needed
import about2Img from '../assets/about2.png'; // Adjust path if needed

const milestones = [
  { year: "2016", title: "The Spark", desc: "Founded as an intimate designer studio with two master carvers channeling heritage metal spinning processes." },
  { year: "2019", title: "Hospitality Pivot", desc: "Partnering directly with global luxury boutique hospitality collectives and landmark interior firms." },
  { year: "2022", title: "Global Footprint", desc: "Scaling clean operations to export structural lighting accents across 50+ countries smoothly." },
  { year: "2026", title: "The Intelligent Diya", desc: "Blending traditional heritage Indian craft shapes with smart-lighting custom automation frameworks." }
];

const team = [
  { name: "Xyz", role: "Founder & Chief Visionary", img: "https://hancockogundiyapartners.com/wp-content/uploads/2019/07/dummy-profile-pic-300x300.jpg" },
  { name: "Abc", role: "Head of Artisan Guild Relations", img: "https://hancockogundiyapartners.com/wp-content/uploads/2019/07/dummy-profile-pic-300x300.jpg" },
  { name: "xyzz", role: "Director of Global Supply Systems", img: "https://hancockogundiyapartners.com/wp-content/uploads/2019/07/dummy-profile-pic-300x300.jpg" }
];

const craftSteps = [
  { 
    id: "step-1", 
    icon: <Flame size={22} />, 
    title: "1. Raw Metallurgical Forging", 
    desc: "We salvage high-grade architectural brass and brass scrap, melting and treating them through precise sand-casting filters controlled strictly by hand templates." 
  },
  { 
    id: "step-2", 
    icon: <Compass size={22} />, 
    title: "2. The Metal-Spinning Wheel", 
    desc: "Sheets are spun against spinning blocks using manual force on lathes, forming seamless luxury lamp dome frameworks without uniform factory line stamps." 
  },
  { 
    id: "step-3", 
    icon: <Eye size={22} />, 
    title: "3. Acid-Etching & Rubbed Finishes", 
    desc: "Instead of synthetic chemical sprays, our distinct deep dark minimalist tint tones are achieved via meticulous layered natural hand-rubbed chemical oxidation cycles." 
  }
];

const serviceSteps = [
  {
    id: "service-1",
    icon: <Award size={22} />,
    title: "Commitment to Quality",
    desc: "Meticulous quality control and expert craftsmanship underscore everything we do. Working closely with our partners, we ensure each piece meets rigorous standards for durability and long-lasting appeal. We value your trust and prioritise products that are both beautiful and dependable for years to come."
  },
  {
    id: "service-2",
    icon: <Users size={22} />,
    title: "Customer Experience",
    desc: "Your vision shapes how we serve you. From helping you find the perfect lamp to customising collections for your space, our goal is to inspire and delight at every step. Whether you shop online or visit us in person, count on Lightingale for personalised guidance and an immersive design journey."
  },
  {
    id: "service-3",
    icon: <Sun size={22} />,
    title: "Our Promise",
    desc: "We promise to bring light — literally and figuratively — into your life. Whether you’re redecorating a room, renovating a house, or looking for the perfect lamp to finish a corner, Lightingale will be with you every step of the way. Explore with us and discover a world where your home shines in its best light."
  }
];

export default function AboutUs({ onNavigate }) {
  const [activeStep, setActiveStep] = useState("step-1");
  const [activeService, setActiveService] = useState("service-1");

  return (
    <div className="premium-about-universe blueprint-theme-canvas">
      
      {/* 1. ARCHITECTURAL HERO HEADER */}
      <section className="luxury-about-hero">
        <div className="hero-dark-ambient-overlay"></div>
        <div className="container localized-hero-inner">
          <span className="boutique-brand-eyebrow">The Lightingale Chronicle</span>
          <h1>Our Story, Handcrafted<br /><span>To Illuminate Your World.</span></h1>
          <div className="fine-title-divider"></div>
          <p>We don't manufacture items—we preserve heritage craft, honor structural raw assets, and sculpt luxury illumination experiences.</p>
        </div>
      </section>

      

      {/* 3. THE EDITORIAL BRAND VISION (ASYMMETRIC CREATIVE GRID) */}
      {/* 3. THE EDITORIAL BRAND VISION (ASYMMETRIC CREATIVE GRID) */}
<section className="container editorial-split-showcase creative-luxury-edition">
  <div className="editorial-media-canvas luxury-layered-gallery">
    <div className="artisan-motif-glow-ring"></div>
    
    {/* Primary Image: about1.png */}
    <div className="main-canvas-frame-primary">
      <img src={about1Img} alt="Lightingale Heritage Artistry" />
    </div>

    {/* Secondary Image: about2.png */}
    <div className="floating-canvas-frame-secondary feature-grain-texture">
      <img src={about2Img} alt="Lightingale Craftsmanship Detail" />
      <div className="vintage-grain-overlay"></div>
    </div>

    <div className="atelier-metadata-tag">
      <span>STUDIO DESK // MODEL 2026 // ATELIER GUILD</span>
    </div>
  </div>

  <div className="editorial-prose-canvas brand-luxury-narrative">
    <div className="eyebrow-accent-line-group">
      <span className="editorial-eyebrow">Welcome to Lightingale</span>
      <div className="gold-horizontal-mini-bar"></div>
    </div>
    <h2>Bridging Heritage Artistry <br />& Contemporary Vibe</h2>
    <p className="p-prose-intro-dropcap">
      We here visionary, inspired design meets curated elegance. Our passion is transforming spaces into beautiful expressions of your vision by offering unique lighting and home décor solutions. Lighting is more than illumination — it’s the soul of every space. Whether it’s your cushy home, stylish apartment, or busy office, the right light shapes ambience, mood, and memories.
    </p>
    <h2>Our Vision</h2>
    <p>
      Every great idea begins with a vision. We believe every home should feel personal, uplifting, and effortlessly stylish. Lightingale merges quality craftsmanship with timeless aesthetics to make designer-level lighting accessible. Our approach blends classic artistry with contemporary trends, offering a handpicked range that suits every mood and occasion.
    </p>
    <button className="btn-luxury-catalog-explore creative-gold-pulse" onClick={() => onNavigate('home')}>
      Explore Collections <ArrowRight size={14} style={{ marginLeft: '6px' }} />
    </button>
  </div>
</section>

      {/* 2. MANUFACTURER & EXPORTER OVERVIEW SECTION */}
      <section className="lightingale-intro-overview-section">
        <div className="container">
          <div className="intro-lead-box">
            <p className="intro-lead-p">
              We are pleased to introduce ourselves as a trusted manufacturer and exporter of premium handcrafted decorative lighting and home décor products from India.
            </p>
          </div>

          <div className="intro-grid-container">
            {/* Product Portfolio Card */}
            <div className="intro-card-pane">
              <h3>Product Portfolio</h3>
              <ul className="intro-styled-list">
                <li>Decorative Table Lamps</li>
                <li>Electric Lamps</li>
                <li>Cordless Rechargeable Lamps</li>
                <li>Candle Lights & Candle Holders</li>
                <li>Festive Decorations</li>
                <li>Luxury Decorative Accessories</li>
              </ul>
            </div>

            {/* Why Choose Us Card */}
            <div className="intro-card-pane">
              <h3>Why Choose Lightingale Creative?</h3>
              <ul className="intro-styled-list">
                <li>Premium handcrafted quality</li>
                <li>Elegant luxury designs</li>
                <li>Sustainable materials</li>
                <li>Custom manufacturing and branding</li>
                <li>Reliable export capability</li>
                <li>Competitive pricing</li>
                <li>On-time delivery and responsive customer support</li>
              </ul>
            </div>
          </div>

          <div className="intro-closing-callout">
            <p>
              Our collections are designed to complement luxury residences, hotels, restaurants, cafés, resorts, villas, commercial interiors, and high-end retail spaces. We combine handcrafted artistry with elegant Victorian styling and contemporary luxury design to create products that enhance sophisticated interiors. We are committed to superior craftsmanship, sustainable materials, dependable quality, customized designs, private-label manufacturing, and project-based solutions.
            </p>
          </div>
        </div>
      </section>

      {/* 4. INTERACTIVE SECTION B: THE BRAND PROMISE SYSTEM */}
      <section className="container craft-blueprint-interactive-section gold-theme-variant" style={{ paddingTop: '60px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <div className="timeline-header-block" style={{ marginBottom: '35px' }}>
          <span className="editorial-eyebrow">Core Values</span>
          <h2>Illuminating Your World with Quality and Care.</h2>
          <p style={{ color: '#aaa', fontSize: '14px', marginTop: '5px' }}>Select a pillar below to learn more about our commitment to you.</p>
        </div>
        
        <div className="blueprint-interactive-grid">
          <div className="blueprint-selectors-column">
            {serviceSteps.map((pillar) => (
              <div 
                key={pillar.id} 
                className={`blueprint-selector-pill ${activeService === pillar.id ? 'active' : ''}`}
                onClick={() => setActiveService(pillar.id)}
              >
                <div className="pill-icon-frame">{pillar.icon}</div>
                <h4>{pillar.title}</h4>
              </div>
            ))}
          </div>
          
          <div className="blueprint-display-panel">
            {serviceSteps.map((pillar) => pillar.id === activeService && (
              <div key={pillar.id} className="active-blueprint-content-fade-in">
                <h3>{pillar.title}</h3>
                <p>{pillar.desc}</p>
                <div className="blueprint-schematic-accent-box">
                  <span>LIGHTINGALE CORE VALUE // EST. 2016 //</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PERFORMANCE PILLARS RIBBON */}
      <section className="architectural-pillars-stripe">
        <div className="container pillars-flex-alignment-row">
          <div className="brand-pillar-node-card">
            <Award className="pillar-linear-vector" size={32} />
            <h3>100% Sustainable</h3>
            <p>We responsibly salvage architectural metal scrap, responsibly sourced mango woods, and fine pure crystals.</p>
          </div>
          <div className="brand-pillar-node-card">
            <ShieldCheck className="pillar-linear-vector" size={32} />
            <h3>Global Compliance</h3>
            <p>Every fixture layer maps to CE, UL, and standard international custom regulatory power protocols safely.</p>
          </div>
          <div className="brand-pillar-node-card">
            <Settings className="pillar-linear-vector" size={32} />
            <h3>B2B Tailored Spec</h3>
            <p>From dimensional scale alterations to custom white box delivery modules, we build to precise parameters.</p>
          </div>
        </div>
      </section>

      {/* 6. HOSPITALITY INSTALLATION REGISTRY */}
      <section className="hospitality-registry-stripe-wrapper">
        <div className="container hospitality-registry-inner">
          <span className="editorial-eyebrow">Curated Collections & Craft</span>
          <h3>Installed in Landmark Venues</h3>
          <p>We collaborate with skilled artisans and trusted manufacturers to bring distinctive lighting solutions and décor accents. Whether you seek subtle elegance or bold statements, Lightingale offers pieces that are versatile and striking — perfect for cushy homes, urban lofts, luxurious spaces, and commercial settings alike.</p>
          <div className="registry-text-ticker-row">
            <div className="ticker-venue-node"><h4>Grand Palace Udaipur</h4><p>Custom Chandeliers</p></div>
            <div className="ticker-venue-node"><h4>The Courtyard Hotel</h4><p>Night Lamp Base</p></div>
            <div className="ticker-venue-node"><h4>Siam Sovereign Spa</h4><p>Ambient Brass Lanterns</p></div>
            <div className="ticker-venue-node"><h4>Ritz Residences NY</h4><p>Minimalist Table Accents</p></div>
          </div>
        </div>
      </section>

      {/* 7. CHRONOLOGY TIMELINE VIEW */}
      <section className="container premium-timeline-wrapper">
        <div className="timeline-header-block">
          <span className="editorial-eyebrow">Historical Trail</span>
          <h2>Our Growth Journey</h2>
        </div>
        <div className="luxury-chronology-trail">
          {milestones.map((item, index) => (
            <div key={index} className="chronology-node-row">
              <div className="chronology-left-year">
                <h3>{item.year}</h3>
              </div>
              <div className="chronology-center-dot-line">
                <div className="pulsing-core-dot"></div>
              </div>
              <div className="chronology-right-description">
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 8. INTERACTIVE SECTION A: CRAFTSMANSHIP BLUEPRINT SYSTEM */}
      <section className="container craft-blueprint-interactive-section">
        <div className="timeline-header-block" style={{ marginBottom: '35px' }}>
          <span className="editorial-eyebrow">The Atelier Workshop</span>
          <h2>Anatomy of Our Process</h2>
          <p style={{ color: '#aaa', fontSize: '14px', marginTop: '5px' }}>Click through the active steps below to peek inside our production guild.</p>
        </div>
        
        <div className="blueprint-interactive-grid">
          <div className="blueprint-selectors-column">
            {craftSteps.map((step) => (
              <div 
                key={step.id} 
                className={`blueprint-selector-pill ${activeStep === step.id ? 'active' : ''}`}
                onClick={() => setActiveStep(step.id)}
              >
                <div className="pill-icon-frame">{step.icon}</div>
                <h4>{step.title}</h4>
              </div>
            ))}
          </div>
          
          <div className="blueprint-display-panel">
            {craftSteps.map((step) => step.id === activeStep && (
              <div key={step.id} className="active-blueprint-content-fade-in">
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                <div className="blueprint-schematic-accent-box">
                  <span>REGISTRY ARCHITECTURE LAYER // L-G 2026 COMPLIANT //</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. DESIGN TEAM HORIZONTAL STRIPE */}
      <section className="premium-team-stripe-universe">
        <div className="container">
          <div className="team-stripe-header-block">
            <div className="target-ornamental-title-row">
              <span className="gold-flourish-line left"></span>
              <h2>The Minds Behind the Radiance</h2>
              <span className="gold-flourish-line right"></span>
            </div>
          </div>

          <div className="target-luxury-horizontal-mesh">
            {team.map((member, index) => (
              <div key={index} className="target-horizontal-artisan-card">
                <div className="horizontal-card-img-pane">
                  <img src={member.img} alt={member.name} />
                </div>
                <div className="horizontal-card-info-pane">
                  <h4>{member.name}</h4>
                  <p className="artisan-role-txt">{member.role}</p>
                  <div className="artisan-social-circle-row">
                    <a href="#linkedin" className="gold-circle-social-btn" aria-label="LinkedIn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                    </a>
                    <a href="#instagram" className="gold-circle-social-btn" aria-label="Instagram">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}